/* eslint-disable complexity */
import React from 'react';
import { makeStyles } from 'design-system/styles';
// TODO: Import @material-ui from design-system folder
/* eslint-disable restrict-imports/restrict-imports */
import Fab from '@material-ui/core/Fab';
import {
  TextCollectionAttachment,
  TextCollectionAttachmentType,
} from '@scale/shared/types/params/textcollection';
import AttachmentView from './AttachmentView';
import { useAttachmentState } from './document';
import _ from 'lodash';
import { useViewAttachmentLinter } from './viewAttachmentLinter';
import classNames from 'classnames';
import { BoundingBox as BoundingBoxFieldType } from '@scale/shared/types/response/textcollection';
import { MOBILE_THRESHOLD } from '@scale/frontend-lib/util';
import { isAttachmentRelevant } from '@scale/shared/textcollection/helpers';
import { ConfigJson, ReviewLevel } from '@scale/shared/defaults';
import { LiveInstructionSection } from '@scale/shared/types/params/genericdatacollection';
import { GUTTER } from './PanZoomImage';
import SmartAssistant from 'react-scale/components/scalers/tasks/SmartAssistant';
import { useSmartAssistantRlhf } from 'react-scale/components/scalers/useSmartAssistantRlhf';
import { TextCollectionAnnotationsOrCallback } from '@scale/shared/types/textCollection';
import { Breadcrumb } from './breadcrumbs';
import { useLLMHelper } from '../../../../dashboard.scale.com/contexts/LLMHelperContext';
import { useChatbotContext } from '../../../../dashboard.scale.com/contexts/ChatbotRankingContext';
import DateCardOverlay from './CurrentDateAttachmentOverlay';
import useUnleashFeatureFlag from 'react-scale/hooks/useUnleashFeatureFlag';

interface AttachmentCarouselProps {
  attachments: TextCollectionAttachment[];
  activeForm?: string;
  editBoundingBoxMode?: boolean;
  boundingBoxField?: BoundingBoxFieldType;
  updateBoundingBoxField?: (bbox: BoundingBoxFieldType) => void;
  loggedUserId?: string;
  useProtectedText?: boolean;
  project?: string;
  completedAttachments?: boolean[];
  stickyAttachments?: number[];
  updateCompletedAttachments?: (completedAttachments: boolean[]) => void;
  allowVideoAttachmentDownload?: boolean;
  forceVisible?: boolean;
  projectConfig?: Partial<ConfigJson>;
  isAuditMode?: boolean;
  isCustomer?: boolean;
  reviewLevel?: ReviewLevel;
  isReview?: boolean;
  assignmentId?: string;
  forceStacked?: boolean;
  showSensitiveContentWarning?: boolean;
  handleSkip?: (skipReason: string, skipExplanation: string) => void;
  startSelfDestructSequence?: (ms: number) => void;
  recordLinkClickingInTextAttachment?: {
    innerTextKeywords: string;
    onSubmitDisabled: (val: boolean) => void;
  };
  disableVideoZoom?: boolean;
  prefillPrompt?: string;
  prefillRefText?: string;
  liveInstructions?: Record<number, LiveInstructionSection[]>;
  overrideImageSize?: {
    width: number;
    height: number;
  };
  taskCustomer?: string;
  subtaskMetadata?: any;
  setAnnotations?: (
    annotations: TextCollectionAnnotationsOrCallback,
    breadcrumbs?: Breadcrumb[],
  ) => void;
}

export default function AttachmentCarousel(props: AttachmentCarouselProps) {
  const { attachments, activeForm = '', recordLinkClickingInTextAttachment } = props;

  const styles = useAttachmentCarouselStyles();
  const smartAssistantRlhfHook = useSmartAssistantRlhf();
  const root = React.useRef<HTMLDivElement | null>(null);
  const [size, setSize] = React.useState({ width: 0, height: 0 });
  const [orderedAttachments, setOrderedAttachments] =
    React.useState<TextCollectionAttachment[]>(attachments);
  const { index, select, boxes } = useAttachmentState();
  const { isAfterSubmitAttempt, viewedAttachments } = useViewAttachmentLinter();
  const llmHelper = useLLMHelper();
  const chatbotContext = useChatbotContext();

  // Scroll to top if the active form changes and there are multiple stacked attachments
  React.useEffect(() => {
    if (activeForm && root.current && attachments.length > 1) {
      root.current.scroll(0, 0);
    }
  }, [activeForm, attachments.length]);

  const onLeft = React.useCallback(() => {
    select(_.clamp(index - 1, 0, attachments.length - 1));
  }, [attachments.length, select, index]);

  const onRight = React.useCallback(() => {
    select(_.clamp(index + 1, 0, attachments.length - 1));
  }, [attachments.length, select, index]);

  React.useEffect(() => {
    if (root.current) {
      let width = root.current.clientWidth;
      let height = root.current.clientHeight;
      if (props.overrideImageSize) {
        width = Math.min(width, props.overrideImageSize.width + 2 * GUTTER);
        height = Math.min(height, props.overrideImageSize.height + 2 * GUTTER);
      }
      setSize({
        width,
        height,
      });
    }
  }, []);

  React.useEffect(() => {
    if (llmHelper.enabled || llmHelper.aiAssistantEnabled) llmHelper.setAttachments(attachments);
    if (chatbotContext.enabled) chatbotContext.setAttachments(attachments);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachments, llmHelper.enabled, chatbotContext.enabled, llmHelper.aiAssistantEnabled]);

  const showNavigationHint = isAfterSubmitAttempt && viewedAttachments === 1;
  const stacked = !!activeForm || props.forceStacked;

  // ======= check link clicking behavior ======= /
  const linkElems: HTMLAnchorElement[] = [];
  if (
    recordLinkClickingInTextAttachment &&
    !props.useProtectedText &&
    !props.projectConfig?.textAttachmentConfig?.renderAsPlainText
  ) {
    const parser = new DOMParser();
    for (const attachment of props.attachments) {
      if (attachment.type === TextCollectionAttachmentType.Text) {
        const htmlContent = parser.parseFromString(attachment.content, 'text/html');
        linkElems.push(...Array.from(htmlContent.getElementsByTagName('a')));
      }
    }
  }
  const linkElemsToClick = linkElems
    .filter(elem =>
      recordLinkClickingInTextAttachment?.innerTextKeywords.includes(`"${elem.innerText}"`),
    )
    .map(elem => elem.href);

  const [linkClickHistory, setLinkClickHistory] = React.useState<{ [link: string]: boolean }>(
    Object.fromEntries(linkElemsToClick.map(elem => [elem, false])),
  );

  const handleLinkClickHistory = (link: string) => {
    if (recordLinkClickingInTextAttachment && Object.keys(linkClickHistory).includes(link)) {
      const newLinkClickHistory = { ...linkClickHistory, [link]: true };
      setLinkClickHistory(newLinkClickHistory);
    }
  };

  React.useEffect(() => {
    if (recordLinkClickingInTextAttachment) {
      recordLinkClickingInTextAttachment.onSubmitDisabled(
        !Object.values(linkClickHistory).every(elem => elem),
      );
    }
  }, [recordLinkClickingInTextAttachment, linkClickHistory]);

  // ======= end check link clicking behavior /

  React.useEffect(() => {
    if (props.projectConfig?.textCollectionConfig?.randomizeAttachmentOrder) {
      if (props.projectConfig?.textCollectionConfig?.randomizeAttachmentOrderIndices?.length) {
        const indicesString =
          props.projectConfig?.textCollectionConfig?.randomizeAttachmentOrderIndices;
        const indices = indicesString?.split(',').map(i => parseInt(i.trim()));
        const subarray = attachments.filter((_, i) => indices?.includes(i));
        const shuffledSubarray = _.shuffle(subarray);
        const newAttachments = attachments.map((attachment, i) => {
          if (indices?.includes(i)) {
            return shuffledSubarray.shift()!;
          } else {
            return attachment;
          }
        });
        setOrderedAttachments(newAttachments);
      } else {
        setOrderedAttachments(_.shuffle(attachments));
      }
    } else {
      setOrderedAttachments(attachments);
    }
  }, [
    attachments,
    props.projectConfig?.textCollectionConfig?.randomizeAttachmentOrder,
    props.projectConfig?.textCollectionConfig?.randomizeAttachmentOrderIndices,
  ]);

  const [focusedAttachmentId, setFocusedAttachmentId] = React.useState<number | null>(null);
  const handleAttachmentFocus = (id: number) => {
    setFocusedAttachmentId(id);
  };
  const handleAttachmentBlur = () => {
    setFocusedAttachmentId(null);
  };

  const isCurrentDateShown =
    useUnleashFeatureFlag('time-attachment-view', { projectId: props.project }, false) || false;

  return (
    <div
      className={classNames(styles.carousel, {
        [styles.stacked]: stacked,
        [styles.fixed]: !stacked,
      })}
      ref={root}
    >
      {isCurrentDateShown && <DateCardOverlay />}
      {orderedAttachments.map(
        (attachment: TextCollectionAttachment, i: number): React.ReactNode => {
          if (!size.height && !props.forceVisible) {
            return null;
          }
          const active = stacked ? isAttachmentRelevant(attachment, activeForm) : i === index;
          const { completedAttachments, updateCompletedAttachments } = props;
          const liveInstructions = props.liveInstructions ? props.liveInstructions[i] : undefined;
          const isSticky =
            props.stickyAttachments && props.stickyAttachments.length > 0
              ? props.stickyAttachments.includes(i)
              : false;
          return (
            <AttachmentView
              key={`attachment ${i}`}
              attachment={attachment}
              index={i}
              hidden={!active}
              size={size}
              boxes={active && !stacked ? boxes : []}
              stacked={stacked}
              editBoundingBoxMode={props.editBoundingBoxMode}
              boundingBoxField={props.boundingBoxField}
              updateBoundingBoxField={props.updateBoundingBoxField}
              loggedUserId={props.loggedUserId}
              useProtectedText={props.useProtectedText}
              markAttachmentAsCompleted={(isComplete: boolean) => {
                if (completedAttachments && updateCompletedAttachments) {
                  const newAttachments = [...completedAttachments];
                  newAttachments[i] = isComplete;
                  updateCompletedAttachments(newAttachments);
                }
              }}
              completed={!_.isUndefined(completedAttachments) && completedAttachments[i]}
              allowVideoAttachmentDownload={props.allowVideoAttachmentDownload}
              projectId={props.project}
              projectConfig={props.projectConfig}
              isAuditMode={props.isAuditMode}
              showSensitiveContentWarning={props.showSensitiveContentWarning}
              handleSkip={props.handleSkip}
              startSelfDestructSequence={props.startSelfDestructSequence}
              handleLinkClickHistory={handleLinkClickHistory}
              handleAttachmentFocus={handleAttachmentFocus}
              handleAttachmentBlur={handleAttachmentBlur}
              unfocused={focusedAttachmentId !== null && focusedAttachmentId !== i}
              disableVideoZoom={props.disableVideoZoom}
              liveInstructions={liveInstructions}
              taskCustomer={props.taskCustomer}
              isSticky={isSticky}
              smartAssistantRlhfHook={smartAssistantRlhfHook}
              prefillPrompt={props.prefillPrompt}
              prefillRefText={props.prefillRefText}
              assignmentId={props.assignmentId}
              subtaskMetadata={props.subtaskMetadata}
              setAnnotations={props.setAnnotations}
              isReview={props.isReview}
              reviewLevel={props.reviewLevel}
            />
          );
        },
      )}
      {!stacked && attachments.length > 1 && (
        <Fab
          disabled={index === 0}
          classes={{ root: styles.left }}
          color="primary"
          aria-label="add"
          onClick={onLeft}
        >
          <ChevronLeft />
        </Fab>
      )}
      {!stacked && attachments.length > 1 && (
        <Fab
          disabled={index === attachments.length - 1}
          classes={{ root: classNames(styles.right, { [styles.pulse]: showNavigationHint }) }}
          color="primary"
          aria-label="add"
          onClick={onRight}
        >
          <ChevronRight />
        </Fab>
      )}
      {!props.isCustomer &&
        !_.isNil(props.reviewLevel) &&
        (props.projectConfig?.textCollectionConfig?.smartAssistantReviewLevels ?? []).includes(
          props.reviewLevel,
        ) && (
          <SmartAssistant
            prompt={props.projectConfig?.textCollectionConfig?.smartAssistantPrompt}
            instructionsS3Document={
              props.projectConfig?.textCollectionConfig?.smartAssistantInstructionsS3Document
            }
            assignmentId={props.assignmentId}
            smartAssistantRlhfHook={smartAssistantRlhfHook}
          />
        )}
    </div>
  );
}

const useAttachmentCarouselStyles = makeStyles(theme => ({
  carousel: {
    flex: 1,
    height: '100%',
    background: theme.colors.gray05,
    position: 'relative',
    maxWidth: 'calc(100vw - 460px)',
    [`@media (max-width: ${MOBILE_THRESHOLD})`]: {
      width: '100%',
      maxWidth: 'initial',
    },
  },
  fixed: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
  },
  stacked: {
    overflowY: 'scroll',
  },
  left: {
    position: 'absolute',
    top: '50%',
    left: '12px',
    fontSize: '24px',
    marginTop: '-28px',
  },
  right: {
    position: 'absolute',
    top: '50%',
    right: '12px',
    fontSize: '24px',
    marginTop: '-28px',
  },
  pulse: {
    animation: '$pulse 1s infinite',
  },
  '@keyframes pulse': {
    '0%': {
      boxShadow: '0 0 0 0 rgba(43, 153, 216, .85)',
    },
    '80%': {
      boxShadow: '0 0 0 2em rgba(43, 153, 216, 0)',
    },
    '100%': {
      boxShadow: '0 0 0 0 rgba(43, 153, 216, 0)',
    },
  },
}));

const ChevronRight = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="1em"
      height="1em"
      viewBox="0 0 24 24"
      fill="currentColor"
    >
      <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
      <path d="M0 0h24v24H0z" fill="none" />
    </svg>
  );
};

const ChevronLeft = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="1em"
      height="1em"
      viewBox="0 0 24 24"
      fill="currentColor"
    >
      <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
      <path d="M0 0h24v24H0z" fill="none" />
    </svg>
  );
};
